/**
 * The meal library — the recipes the week is planned from, and the ingredients they are
 * built out of.
 *
 * WHY THIS SCREEN IS TWO LISTS AND NOT ONE. It used to be one flat list where "Tomato,
 * 100 g" sat beside "Dinner" as though they were the same kind of thing, so the library
 * offered him a tomato to plan a Tuesday around. Ingredients are the most valuable data
 * here — the doc-sourced sodium figures come from his own protocol and beat any lookup API
 * — but they are not meals. They are the parts bin, so they sit below, folded away, and the
 * recipes get the photos and the space.
 *
 * A PHOTO IS NOT DECORATION at this size. It is the difference between reading a list and
 * recognising a meal, which is the whole reason a recipe box works the way it does.
 *
 * Every macro is optional and blank means NULL. A recipe short on sodium says so with an em
 * dash and a badge, and the day it lands on reports itself as partial rather than letting
 * the missing value read as zero.
 */
import { useCallback, useEffect, useState } from 'react';
import { api } from '../../lib/api';
import type {
  WellnessIngredient,
  WellnessMeal,
  WellnessRecipe,
} from '../../shared/types';
import { Card, CardTitle, Empty, ErrorState, LoadingRows } from '../home/ui';
import { macro, macroLabel, shortDate, shortTime } from './format';
import IngredientEditor from './IngredientEditor';
import RecipeEditor from './RecipeEditor';
import RecipePhoto from './RecipePhoto';

/** Macros a recipe has no figure for at all — distinct from ones it has but that are short. */
function blankMacros(r: WellnessRecipe): string[] {
  return (['calories', 'protein_g', 'fat_g', 'carb_g', 'sodium_mg', 'potassium_mg'] as const)
    .filter((k) => r[k] === null)
    .map(macroLabel);
}

/** Everything a recipe's numbers are not telling the whole truth about. */
function shortOn(r: WellnessRecipe): string[] {
  return [...new Set([...blankMacros(r), ...r.macros_partial.map(macroLabel)])];
}

export default function MealLibrary({ onChanged }: { onChanged: () => void }) {
  const [recipes, setRecipes] = useState<WellnessRecipe[] | null>(null);
  const [ingredients, setIngredients] = useState<WellnessIngredient[]>([]);
  const [meals, setMeals] = useState<WellnessMeal[]>([]);
  const [error, setError] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState<WellnessRecipe | 'new' | null>(null);
  const [editingIngredient, setEditingIngredient] = useState<WellnessIngredient | 'new' | null>(null);
  const [showParts, setShowParts] = useState(false);

  const load = useCallback(async () => {
    setError(false);
    try {
      const [r, i, m] = await Promise.all([
        api.get<WellnessRecipe[]>('/wellness/recipes'),
        api.get<WellnessIngredient[]>('/wellness/ingredients'),
        api.get<WellnessMeal[]>('/wellness/meals'),
      ]);
      setRecipes(r);
      setIngredients(i);
      setMeals(m);
    } catch {
      setError(true);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const refresh = () => {
    void load();
    onChanged();
  };

  if (error)
    return (
      <Card className="p-5">
        <ErrorState what="your meals" onRetry={() => void load()} />
      </Card>
    );
  if (!recipes)
    return (
      <Card className="p-5">
        <LoadingRows rows={5} />
      </Card>
    );

  const incomplete = recipes.filter((r) => shortOn(r).length > 0);

  return (
    <div className="space-y-4">
      <Card className="p-5">
        <div className="flex items-center justify-between gap-3">
          <CardTitle icon="pot">Recipes</CardTitle>
          <button
            type="button"
            onClick={() => setEditingRecipe('new')}
            className="min-h-11 shrink-0 rounded-full border border-sage bg-surface px-4 text-sm font-medium text-sage focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
          >
            New recipe
          </button>
        </div>

        {/* Stated once at the top rather than only as a badge per row, because the effect is
            on the DAILY TOTAL and that is where he noticed it. */}
        {incomplete.length > 0 && (
          <p className="mt-3 rounded-xl bg-honey-soft px-3 py-2 text-xs text-honey">
            {incomplete.length} of these {incomplete.length === 1 ? 'is' : 'are'} missing some
            macros, so any day they land on reports a total lower than the truth. Open one, open
            the ingredient that’s blank, and press Look up — the match is shown before anything
            is saved.
          </p>
        )}

        {recipes.length === 0 ? (
          <div className="mt-4">
            <Empty message="No recipes yet. Build one from your ingredients — name it, add what goes in it, and the macros add themselves up. Then planning a week is picking from a shelf." />
          </div>
        ) : (
          <ul className="mt-4 grid grid-cols-2 gap-3">
            {recipes.map((r) => {
              const short = shortOn(r);
              // Joined rather than three conditional spans each carrying its own separator,
              // which is how a card with no ingredients ended up reading "· eaten 1×".
              const meta = [
                r.ingredients.length
                  ? `${r.ingredients.length} ingredient${r.ingredients.length === 1 ? '' : 's'}`
                  : null,
                r.servings > 1 ? `makes ${r.servings}` : null,
                r.use_count > 0 ? `eaten ${r.use_count}×` : null,
              ].filter(Boolean);
              return (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={() => setEditingRecipe(r)}
                    className="flex h-full w-full flex-col overflow-hidden rounded-2xl border border-border bg-canvas text-left hover:border-sage focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
                  >
                    <RecipePhoto name={r.name} url={r.photo_url} className="aspect-[4/3] w-full" />
                    <span className="flex min-w-0 flex-1 flex-col gap-1 p-3">
                      <span className="font-medium text-ink">{r.name}</span>
                      <span className="text-xs text-ink-soft">
                        {macro(r.calories)} kcal · {macro(r.protein_g, 'g')} protein ·{' '}
                        {macro(r.sodium_mg, 'mg')} sodium
                      </span>
                      <span className="mt-auto flex flex-col items-start gap-1 pt-1">
                        {meta.length > 0 && (
                          <span className="text-xs text-ink-faint">{meta.join(' · ')}</span>
                        )}
                        {short.length > 0 && (
                          <span
                            className="rounded-full bg-honey-soft px-2 py-0.5 text-xs text-honey"
                            title={`No ${short.join(', ')} recorded, so its totals are short.`}
                          >
                            {short.length} missing
                          </span>
                        )}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      {/* The parts bin. Folded away by default: these are what recipes are BUILT from, and
          giving them equal billing is what made the library unusable in the first place. */}
      <Card className="p-5">
        <div className="flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => setShowParts((s) => !s)}
            aria-expanded={showParts}
            className="flex min-h-11 items-center gap-2 text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
          >
            <CardTitle icon="check">Ingredients</CardTitle>
            <span className="text-sm text-ink-soft">
              {ingredients.length} · {showParts ? 'hide' : 'show'}
            </span>
          </button>
          <button
            type="button"
            onClick={() => setEditingIngredient('new')}
            className="min-h-11 shrink-0 rounded-full border border-border bg-surface px-4 text-sm font-medium text-ink-soft focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
          >
            Add
          </button>
        </div>
        <p className="mt-2 text-xs text-ink-soft">
          The building blocks, with macros per portion. Recipes are made of these — you plan
          recipes, not ingredients.
        </p>

        {showParts &&
          (ingredients.length === 0 ? (
            <div className="mt-4">
              <Empty message="No ingredients yet. Add one and look up its macros once — every recipe you build on it inherits them." />
            </div>
          ) : (
            <ul className="mt-4 space-y-1">
              {ingredients.map((i) => (
                <li key={i.id}>
                  <button
                    type="button"
                    onClick={() => setEditingIngredient(i)}
                    className="flex w-full items-center gap-3 rounded-xl px-2 py-2 text-left hover:bg-canvas focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm text-ink">
                        {i.name}
                        <span className="text-ink-soft"> · {i.portion_desc}</span>
                      </span>
                      <span className="block text-xs text-ink-soft">
                        {macro(i.calories)} kcal · {macro(i.protein_g, 'g')} protein ·{' '}
                        {macro(i.sodium_mg, 'mg')} sodium
                      </span>
                    </span>
                    {i.used_in > 0 && (
                      <span className="shrink-0 text-xs text-ink-faint">
                        in {i.used_in} recipe{i.used_in === 1 ? '' : 's'}
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          ))}
      </Card>

      <Card className="p-5">
        <CardTitle icon="calendar">Recently eaten</CardTitle>
        {meals.length === 0 ? (
          <p className="mt-4 text-sm text-ink-soft">Nothing logged yet.</p>
        ) : (
          <ul className="mt-4 space-y-1.5">
            {meals.slice(0, 20).map((m) => (
              <li
                key={m.id}
                className="flex items-baseline justify-between gap-3 border-b border-border pb-1.5 last:border-0"
              >
                <span className="min-w-0">
                  <span className="block truncate text-ink">{m.description}</span>
                  <span className="block text-xs text-ink-soft">
                    {macro(m.calories)} kcal · {macro(m.protein_g, 'g')} protein ·{' '}
                    {macro(m.sodium_mg, 'mg')} sodium
                    {m.macros_partial.length > 0 && (
                      <span className="text-honey">
                        {' '}
                        · short on {m.macros_partial.map(macroLabel).join(', ')}
                      </span>
                    )}
                  </span>
                </span>
                <span className="shrink-0 text-xs text-ink-soft">
                  {shortDate(m.eaten_at)} {shortTime(m.eaten_at)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {editingRecipe && (
        <RecipeEditor
          recipe={editingRecipe === 'new' ? null : editingRecipe}
          ingredients={ingredients}
          onClose={() => setEditingRecipe(null)}
          onSaved={() => {
            setEditingRecipe(null);
            refresh();
          }}
          onIngredientsChanged={() => void load()}
        />
      )}

      {editingIngredient && (
        <IngredientEditor
          ingredient={editingIngredient === 'new' ? null : editingIngredient}
          onClose={() => setEditingIngredient(null)}
          onSaved={() => {
            setEditingIngredient(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}
